




































































































import {
  defineComponent,
  computed,
  PropType,
  ref,
  useContext,
  useRoute,
  useRouter,
} from '@nuxtjs/composition-api';

import {
  SfButton,
  SfHeading,
  SfList,
  SfLoader,
  SfProductCardHorizontal,
  SfQuantitySelector,
} from '~/components';

import {
  useCart,
  useApi,
  useProduct,
  useUiNotification,
  useUser,
  useWishlist,
  ProductDetailsQuery,
} from '~/composables';

import {
  getName as getProductName,
  getPrice as getProductPrice,
  getOptimizedProductThumbnailImage,
  getDetails,
  getCare,
  getTagLine,
} from '~/modules/catalog/product/getters/productGetters';

import { getUpsellProductPriceCommand } from '~/modules/catalog/pricing/getUpsellProductPriceCommand';
import getProductPriceBySkuGql from '~/modules/catalog/product/queries/getProductPriceBySku.gql';
import { Product } from '~/modules/catalog/product/types';

import { ADD_TO_CART_BTN_TEXT } from '~/modules/catalog/product/constants';

export default defineComponent({
  name: 'UpsellProducts',
  components: {
    SfButton,
    SfHeading,
    SfList,
    SfLoader,
    SfProductCardHorizontal,
    SfQuantitySelector,
    SizeSelector: () => import('~/modules/catalog/product/components/product-types/grouped/SizeSelector.vue'),
  },
  props: {
    upsells: {
      type: Array as PropType<Product[]> | null,
      default: null,
    },
  },
  emits: ['notify-me'],
  setup(props, { emit }) {
    const { app } = useContext();
    const { query } = useApi();
    const { getProductPath } = useProduct();
    const { isAuthenticated } = useUser();
    const { send: sendNotification } = useUiNotification();
    const route = useRoute();
    const router = useRouter();

    const {
      addItem,
      error: cartError,
      loading: cartLoading
    } = useCart();

    const {
      addOrRemoveItem,
      isInWishlist
    } = useWishlist();


    type upsellItems = {
      value: any;
    };

    const upsellItems = computed(() => props.upsells) as upsellItems;
    const isSomeUpsellItemsSelect = computed(() => upsellItems.value?.some(item => item.qty !== 0));

    const selectedSize = ref(null);
    const selectedProductsConfiguration = ref([]);
    const selectedProductsConfigurationUids = computed(() => selectedProductsConfiguration.value.reduce((acc, curr) => acc + curr.uid, ''));
    const handleSelectedSize = async (size) => {
      isLoadingSize.value = true;
      const index = selectedProductsConfiguration.value.findIndex(item => item.productUid === size.value.productUid);

      if (index !== -1) {
        selectedProductsConfiguration.value[index] = size.value;
      } else {
        selectedProductsConfiguration.value.push(size.value);
      }

      const { data } = await query<ProductDetailsQuery>(getProductPriceBySkuGql, {
        filter: { sku: { eq: size.value.sku } },
        configurations: { uid: size.value.uid },
      });

      const configuration = data.products.items.length
        ? data.products.items[0]['configurable_product_options_selection']
        : null;

      upsellItems.value.forEach(upsellItem => {
        const hasMatchingOption =
          upsellItem?.configurable_options?.[0]?.values?.some(option => option.sku === size.value.sku) ?? false;

        const isDirectMatch = upsellItem.sku === size.value.sku && configuration;

        if (hasMatchingOption || isDirectMatch) {
          if (configuration) {
            upsellItem.price_range = configuration.variant.price_range;
            upsellItem.saleable_qty = configuration.variant.saleable_qty;
          }

          upsellItem.qty = 0;
        }
      });

      isLoadingSize.value = false;
    };

    const getItemQuantity = item => {
      if (item.__typename === 'SimpleProduct') return item.saleable_qty;

      let qty = 0;
      item?.configurable_options[0].values.forEach(option => {
        if (selectedProductsConfigurationUids.value.includes(option.uid)) {
          qty = option.saleable_qty;
        }
      });

      return qty;
    };

    const getItemSku = item => {
      if (item.__typename === 'SimpleProduct') return item.sku;

      let sku = '';
      item?.configurable_options[0].values.forEach(option => {
        if (selectedProductsConfigurationUids.value.includes(option.uid)) {
          sku = option.sku;
        }
      });

      return sku;
    };

    const loading = ref(false);

    const isLoadingSize = ref(false);

    const addToCart = async () => {
      try {
        loading.value = true;
        upsellItems.value
          .filter(item => item.qty)
          .forEach(item => {
            const productConfiguration = selectedProductsConfiguration.value.find(product => product.productUid === item.uid);
            addItem({ product: item, quantity: item.qty, productConfiguration });
            item.qty = 0;
            if (cartError.value.addItem) {
              sendNotification({
                id: Symbol('qty_update_error'),
                message: app.i18n.t('Your desired amount of {ProductName} you have requested is not available at this time', { ProductName: item.name.trim() }) as string,
                type: 'danger',
                icon: 'info_shield',
                persist: false,
              });
            }
          });
      } catch (e) {
        console.warn('Error: ', e);
      } finally {
        loading.value = false;
      }
    };

    const productsPrice = computed(() => getUpsellProductPriceCommand(upsellItems.value));

    const addItemToWishlist = async (product) => {
      if (isAuthenticated.value) {
        await addOrRemoveItem({ product });
      } else {
        await router.push(app.localeRoute({ name: 'customer-login-register', query: { redirect: route.value.path } }));
        sendNotification({
          id: Symbol('need_to_login'),
          message: app.i18n.tc('Need to login message'),
          type: 'warning',
          icon: 'check',
          persist: false,
        });
      }
    };

    const handleNotifyMe = (sku) => {
      emit('notify-me', sku);
    };

    return {
      upsellItems,
      getProductName,
      getProductPrice,
      getOptimizedProductThumbnailImage,
      getDetails,
      getCare,
      getTagLine,

      getProductPath,
      addItemToWishlist,
      addToCart,
      cartLoading,
      selectedSize,
      handleSelectedSize,
      isInWishlist,

      loading,
      isLoadingSize,
      isSomeUpsellItemsSelect,
      productsPrice,
      handleNotifyMe,
      selectedProductsConfiguration,
      getItemQuantity,
      getItemSku,
      ADD_TO_CART_BTN_TEXT
    };
  }
});
